import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  DataGrid,
  GridColDef,
  GridColumnMenu,
  GridColumnMenuProps,
  GridFilterModel,
  GridPaginationModel,
  getGridSingleSelectOperators,
  getGridStringOperators,
} from '@mui/x-data-grid';
import {
  FilterAlt,
  FilterList,
  FilterListOff,
  RadioButtonChecked,
} from '@mui/icons-material';
import { Email, HideSource } from '@mui/icons-material';
import { UserRole } from '../../../enums/user';
import { User, UserLastInactivation } from '../../../interfaces/user.interface';
import ChipStatus from '../../chipStatus';
import styled from 'styled-components';
import { Divider, ListItemIcon, Menu, MenuItem, Stack, Typography } from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { PersonAddAlt1, GroupAdd, Edit } from '@mui/icons-material';
import { INACTIVATION_REASONS } from '../../../data/inactivationReasons';
import { INACTIVATION_REASON_TRANSLATION_KEYS } from '../../../data/inactivationReasonTranslations';
import { representationSexOptions } from '../../../services/utils/representationOptions';
import { formatDate, getDataGridLocaleText } from '../../../i18n/formatting';
import { useAppLocale } from '../../../i18n/useAppLocale';

export interface UserTableModel {
  id: string;
  first_name: string;
  last_name: string;
  role: number;
  email: string;
  group: { name: string };
  created_at: string;
  status: number;
  cellphone?: string;
  birth_date?: string | Date;
  sex?: number;
  country?: string;
  responsible_groups?: string[];
  updated_at?: string;
  rawUser?: User;
  last_inactivation?: UserLastInactivation | null;
  inactivationReasonCode?: string;
  inactivationReasonLabel?: string;
}

type StatusFilterValue = 'active' | 'invited' | 'inactive';

export interface UserTableProps {
  users: UserTableModel[];
  allGroups: { id: string; name: string }[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (newPage: number) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  openMenu: boolean;
  handleClose: () => void;
  anchorEl: null | HTMLElement;
  handleClick: (event: React.MouseEvent<HTMLElement>, user: UserTableModel) => void;
  onInactivate: (user: UserTableModel) => void;
  onResendInvite?: () => void;
  onResendReminder?: () => void;
  showModal?: (user: UserTableModel) => void;
  showEditModal?: (user: UserTableModel) => void;
  selectedUser: UserTableModel | null;
  resolveRequesterDisplayName: (requesterUserId: string) => Promise<string>;
  exportFileCompanyName?: string;
}

const roleLabels: Record<number, string> = {
  [UserRole.sAdmin]: 'Consultor Zenbox',
  [UserRole.admin]: 'Gestor de Felicidade',
  [UserRole.employee]: 'Colaborador',
  [UserRole.group_responsible]: 'Responsável de Grupo',
};

const singleSelectOperators = getGridSingleSelectOperators();

const statusFilterOperators = singleSelectOperators.filter(
  (operator) => operator.value === 'isAnyOf'
);

const simpleTextFilterOperators = getGridStringOperators().filter(
  (operator) => operator.value !== 'isAnyOf'
);

const isInactiveStatus = (status: unknown) => status == 3 || status == 4 || status == 5;

const getInactivationReasonLabel = (
  reasonCode?: string | null,
  translate?: (key: string) => unknown
): string => {
  if (!reasonCode) return '';

  const reason = INACTIVATION_REASONS.find((item) => item.reasonCode === reasonCode);
  if (!reason) return '';

  return translate
    ? String(translate(INACTIVATION_REASON_TRANSLATION_KEYS[reason.reasonCode]))
    : reason.labelPtBr;
};

const getLastInactivationValue = (user: UserTableModel): UserLastInactivation | null =>
  user.rawUser?.last_inactivation ?? user.last_inactivation ?? null;

const getGroupValue = (group: unknown): string => {
  if (!group) return '';

  if (typeof group === 'string') {
    return group.trim();
  }

  if (Array.isArray(group)) {
    const firstValidGroup = group.find((item) => typeof item === 'string' && item.trim().length > 0);
    return typeof firstValidGroup === 'string' ? firstValidGroup.trim() : '';
  }

  if (typeof group === 'object') {
    const groupName = (group as { name?: unknown }).name;

    if (typeof groupName === 'string') {
      return groupName.trim();
    }
  }

  return '';
};

const getGroupsValue = (groups: unknown): string => {
  if (!Array.isArray(groups)) {
    return '';
  }

  return groups
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .join(', ');
};

const getSexLabel = (sex: unknown): string => {
  if (sex == 1) return representationSexOptions[0] ?? '';
  if (sex == 2) return representationSexOptions[1] ?? '';
  if (sex == 3) return representationSexOptions[2] ?? '';
  return '';
};

const formatDateOnly = (value?: string | Date | null): string => {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = String(date.getUTCFullYear());

  return `${day}/${month}/${year}`;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const datePart = date.toLocaleDateString('pt-BR');
  const timePart = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart} ${timePart}`;
};

const parseDateOnlyForComparison = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const rawValue = String(value).trim();
  let date: Date | null = null;

  const ptBrMatch = rawValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ptBrMatch) {
    const [, day, month, year] = ptBrMatch;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    const normalizedValue = rawValue.includes('T') ? rawValue : rawValue.replace(' ', 'T');
    const parsedDate = new Date(normalizedValue);
    date = Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const escapeExcelText = (value?: string | null): string => {
  const normalizedValue = String(value ?? '').trim();

  if (!normalizedValue) return '';

  return normalizedValue.startsWith('+') ? `'${normalizedValue}` : normalizedValue;
};

const resolveResponsibleGroups = (
  responsibleGroups: unknown,
  allGroups: { id: string; name: string }[]
): string => {
  if (!Array.isArray(responsibleGroups) || responsibleGroups.length === 0) {
    return '';
  }

  return responsibleGroups
    .map((groupId) => {
      if (typeof groupId !== 'string') return '';
      return allGroups.find((group) => group.id === groupId)?.name?.trim() || '';
    })
    .filter(Boolean)
    .join(', ');
};

const sortedInactivationReasons = [...INACTIVATION_REASONS].sort((a, b) =>
  a.labelPtBr.localeCompare(b.labelPtBr, 'pt-BR', { sensitivity: 'base' })
);

const formatInactivationDateTime = (value?: string | null, locale?: string): { date: string; time: string } => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  return {
    date: date.toLocaleDateString(locale),
    time: date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }),
  };
};

const InactivationReasonCell: React.FC<{
  last_inactivation?: UserLastInactivation | null;
  reasonCode?: string;
  reasonLabel?: string;
  resolveRequesterDisplayName: (requesterUserId: string) => Promise<string>;
}> = ({ last_inactivation, reasonCode, reasonLabel, resolveRequesterDisplayName }) => {
  const { t } = useTranslation('settings');
  const appLocale = useAppLocale();
  const [requesterLabel, setRequesterLabel] = useState(() => t('inactivation.requesterUnknown'));

  useEffect(() => {
    let isMounted = true;
    const requesterUserId = last_inactivation?.request_info?.requester_user_id;

    if (!requesterUserId) {
      setRequesterLabel(String(t('inactivation.requesterUnknown')));
      return;
    }

    const resolveRequester = async () => {
      const label = await resolveRequesterDisplayName(requesterUserId);

      if (isMounted) {
        setRequesterLabel(label || String(t('inactivation.requesterUnknown')));
      }
    };

    void resolveRequester();

    return () => {
      isMounted = false;
    };
  }, [last_inactivation?.request_info?.requester_user_id, resolveRequesterDisplayName, t]);

  if (!last_inactivation || !reasonCode) {
    return <Box sx={{ width: '100%' }}>- -</Box>;
  }

  const displayReasonLabel = getInactivationReasonLabel(reasonCode, t) || reasonLabel || reasonCode;
  const customReasonText = last_inactivation.custom_reason_text?.trim() || '';
  const hasCustomReasonText = reasonCode === 'CUSTOM' && customReasonText.length > 0;
  const inactivationDateTime = formatInactivationDateTime(last_inactivation.inactivated_at, appLocale);
  const tooltipText = String(
    t('inactivation.tooltip', {
      date: inactivationDateTime.date || t('inactivation.dateUnavailable'),
      time: inactivationDateTime.time,
      requester: requesterLabel,
    })
  );
  const tooltipTitle = hasCustomReasonText ? (
    <Box sx={{ whiteSpace: 'pre-line' }}>{`${tooltipText} ${String(t('inactivation.customReasonIntro'))}\n\n"${customReasonText}"`}</Box>
  ) : (
    tooltipText
  );

  return (
    <Tooltip
      title={tooltipTitle}
      arrow
      placement="bottom"
      enterTouchDelay={0}
      leaveTouchDelay={500}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -32],
              },
            },
          ],
        },
      }}
      PopperProps={{
        sx: {
          [`& .${tooltipClasses.arrow}`]: {
            left: "-90px !important"
          }
        }
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ width: '100%', minWidth: 0, cursor: 'help' }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
          }}
        >
          {displayReasonLabel}
        </Box>

        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: '#6B7280',
            flexShrink: 0,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
      </Stack>
    </Tooltip>
  );
};

export const ListTable = styled.div`
  width: 100%;

  .MuiDataGrid-root {
    font-size: 16px;
    background: #fff;
    border-radius: 5px;
    border: none;
    width: 100%;
  }

  .MuiDataGrid-columnHeaders {
    background-color: #f1f1f1;
    font-weight: bold;
    color: #878b8e;
    border-bottom: 3px solid #f1f1f1;
  }

  .MuiDataGrid-cell {
    color: #878b8e;
    font-size: 16px;
    overflow: hidden;
  }

  .MuiDataGrid-row:hover {
    background-color: transparent !important;
  }

  .MuiDataGrid-cell:focus,
  .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  .MuiDataGrid-cell:focus-within,
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }
`;

const getStatusFilterValue = (status: unknown): StatusFilterValue | '' => {
  if (status == 1) return 'active';
  if (status == 2) return 'invited';
  if (status == 3 || status == 4 || status == 5) return 'inactive';
  return '';
};

const getStatusFilterLabel = (value: unknown): string => {
  switch (value) {
    case 'active':
      return 'Ativo';
    case 'invited':
      return 'Convidado';
    case 'inactive':
      return 'Inativo';
    default:
      return '';
  }
};

const ActiveColumnFilteredIcon = () => (
  <FilterAlt sx={{ color: '#4A90E2', fontSize: 18 }} />
);

const ACTIVE_FILTER_COLOR = '#4A90E2';
const INACTIVE_FILTER_COLOR = '#878B8E';

const getUserRowId = (row: { id: string }) => row.id;

const sanitizeCompanyNameForFile = (companyName?: string) => {
  const normalized = (companyName || 'empresa')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9À-ÿ-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'empresa';
};

const formatExportDateTime = (date: Date) => {
  const datePart = date.toLocaleDateString('pt-BR').replace(/\//g, '-');

  const timePart = date
    .toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(/:/g, '-');

  return `${datePart}--${timePart}`;
};


const formatExportDateTimeDisplay = (value?: string | null): string => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const datePart = date.toLocaleDateString('pt-BR');
  const timePart = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart} ${timePart}`;
};

const normalizeFilterString = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getUserTableFilterValue = (row: UserTableModel, field: string): unknown => {
  switch (field) {
    case 'name':
      return `${row.first_name || ''} ${row.last_name || ''}`.trim();
    case 'email':
      return row.email || '';
    case 'group':
      return row.group?.name || '';
    case 'created_at':
      return row.created_at || '';
    case 'status':
      return getStatusFilterValue(row.status);
    case 'inactivationReasonCode':
      return (
        row.inactivationReasonCode ||
        row.rawUser?.last_inactivation?.reason_code ||
        row.last_inactivation?.reason_code ||
        ''
      );
    default:
      return (row as unknown as Record<string, unknown>)[field];
  }
};

const applyUserTableFilter = (row: UserTableModel, filterItem: GridFilterModel['items'][number]) => {
  const value = getUserTableFilterValue(row, filterItem.field);
  const operator = filterItem.operator ?? '';

  if (filterItem.field === 'status' || filterItem.field === 'inactivationReasonCode') {
    const values = Array.isArray(filterItem.value) ? filterItem.value : [];

    if (operator === 'isAnyOf') {
      return values.length === 0 ? true : values.includes(value as string);
    }
  }

  if (filterItem.field === 'created_at') {
    const rowDate = parseDateOnlyForComparison(value);
    const filterDate = parseDateOnlyForComparison(filterItem.value);

    if (!rowDate) {
      return operator === 'isEmpty';
    }

    if (!filterDate) {
      return operator !== 'isNotEmpty';
    }

    const rowTime = rowDate.getTime();
    const filterTime = filterDate.getTime();

    switch (operator) {
      case 'is':
        return rowTime === filterTime;
      case 'not':
        return rowTime !== filterTime;
      case 'after':
        return rowTime > filterTime;
      case 'onOrAfter':
        return rowTime >= filterTime;
      case 'before':
        return rowTime < filterTime;
      case 'onOrBefore':
        return rowTime <= filterTime;
      default:
        return true;
    }
  }

  if (typeof value === 'string') {
    const normalizedValue = normalizeFilterString(value);
    const normalizedFilter = normalizeFilterString(filterItem.value);

    switch (operator) {
      case 'contains':
        return normalizedFilter === '' ? true : normalizedValue.includes(normalizedFilter);
      case 'equals':
        return normalizedFilter === '' ? true : normalizedValue === normalizedFilter;
      case 'startsWith':
        return normalizedFilter === '' ? true : normalizedValue.startsWith(normalizedFilter);
      case 'endsWith':
        return normalizedFilter === '' ? true : normalizedValue.endsWith(normalizedFilter);
      case 'is':
        return normalizedFilter === '' ? true : normalizedValue === normalizedFilter;
      case 'not':
        return normalizedFilter === '' ? true : normalizedValue !== normalizedFilter;
      case 'isEmpty':
        return normalizedValue === '';
      case 'isNotEmpty':
        return normalizedValue !== '';
      default:
        return true;
    }
  }

  return true;
};

const filterUsersForExport = (rows: UserTableModel[], filterModel: GridFilterModel) => {
  const activeItems = filterModel.items.filter(
    (item) => item.field && item.operator && item.value !== undefined && item.value !== null && item.value !== ''
  );

  if (activeItems.length === 0) {
    return rows;
  }

  const logicOperator = filterModel.logicOperator === 'or' ? 'or' : 'and';

  return rows.filter((row) => {
    const matches = activeItems.map((item) => applyUserTableFilter(row, item));
    return logicOperator === 'or' ? matches.some(Boolean) : matches.every(Boolean);
  });
};

const escapeDelimitedValue = (value: string, delimiter: string) => {
  const normalizedValue = value ?? '';
  const shouldQuote =
    normalizedValue.includes(delimiter) ||
    normalizedValue.includes('"') ||
    normalizedValue.includes('\n') ||
    normalizedValue.includes('\r');

  const escapedValue = normalizedValue.replace(/"/g, '""');
  return shouldQuote ? `"${escapedValue}"` : escapedValue;
};

const buildDelimitedFile = (rows: Array<Array<string>>, delimiter: string) =>
  rows.map((row) => row.map((value) => escapeDelimitedValue(value, delimiter)).join(delimiter)).join('\n');

const buildCsvForExcel = (rows: Array<Array<string>>) => `\uFEFF${buildDelimitedFile(rows, ';')}`;

const ExportHeaderButton: React.FC<{
  companyName?: string;
  users: UserTableModel[];
  allGroups: { id: string; name: string }[];
  filterModel: GridFilterModel;
  resolveRequesterDisplayName: (requesterUserId: string) => Promise<string>;
}> = ({ companyName, users, allGroups, filterModel, resolveRequesterDisplayName }) => {
  const { t } = useTranslation('settings');
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);

  const isExportMenuOpen = Boolean(exportMenuAnchorEl);
  const exportDateTime = formatExportDateTime(new Date());
  const csvFileName = `export-de-usuarios-zenbox---${sanitizeCompanyNameForFile(companyName)}---${exportDateTime}`;

  const handleOpenExportMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const buildExportRows = async () => {
    const filteredUsers = filterUsersForExport(users, filterModel);
    const headers = [
      'Nome',
      'E-mail',
      'Celular',
      'Data de Nascimento',
      'Sexo informado',
      'País',
      'Responsável pelos grupos',
      'Grupos',
      'Data de Cadastro',
      'Motivo da Inativação',
      'Situação',
      'Inativado por',
      'Inativado em',
      'Data da última atualização',
    ];

    const rowsToExport = await Promise.all(
      filteredUsers.map(async (user) => {
        const lastInactivation = getLastInactivationValue(user);
        const reasonCode = user.inactivationReasonCode || lastInactivation?.reason_code || '';
        const reasonLabel = user.inactivationReasonLabel || getInactivationReasonLabel(reasonCode);
        const customReasonText = lastInactivation?.custom_reason_text?.trim() || '';
        const inactivationReason =
          reasonCode === 'CUSTOM' && customReasonText.length > 0
            ? `${reasonLabel || 'Outro motivo'}: ${customReasonText}`
            : reasonLabel || '';

        let requesterDisplay = '';

        if (isInactiveStatus(user.status) && reasonCode && lastInactivation?.request_info?.requester_user_id) {
          try {
            requesterDisplay =
              (await resolveRequesterDisplayName(lastInactivation.request_info.requester_user_id)) ||
              'Usuário não reconhecido';
          } catch {
            requesterDisplay = 'Usuário não reconhecido';
          }
        }

        return [
          `${user.first_name || ''}${user.last_name ? ` ${user.last_name}` : ''}`.trim(),
          user.email || '',
          escapeExcelText(user.cellphone || user.rawUser?.cellphone || ''),
          formatDateOnly(user.birth_date || user.rawUser?.birth_date),
          getSexLabel(user.sex ?? user.rawUser?.sex),
          user.country || user.rawUser?.country || '',
          resolveResponsibleGroups(user.responsible_groups || user.rawUser?.responsible_groups, allGroups),
          user.group?.name || '',
          formatDateTime(user.created_at),
          isInactiveStatus(user.status) ? inactivationReason : '',
          getStatusFilterLabel(getStatusFilterValue(user.status)),
          isInactiveStatus(user.status) ? requesterDisplay : '',
          isInactiveStatus(user.status)
            ? formatExportDateTimeDisplay(lastInactivation?.inactivated_at)
            : '',
          formatDateTime(user.updated_at || user.rawUser?.updated_at),
        ];
      })
    );

    return { headers, rowsToExport };
  };

  const handleExportCsv = async () => {
    handleCloseExportMenu();

    const { headers, rowsToExport } = await buildExportRows();
    const csvContent = buildCsvForExcel([headers, ...rowsToExport]);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${csvFileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyToClipboard = async () => {
    handleCloseExportMenu();

    const { headers, rowsToExport } = await buildExportRows();
    const clipboardText = buildDelimitedFile([headers, ...rowsToExport], '\t');
    await navigator.clipboard.writeText(clipboardText);
  };

  return (
    <>
      <Tooltip title={String(t('users.exportTooltip'))}>
        <IconButton
          size="small"
          onClick={handleOpenExportMenu}
          sx={{
            color: '#878B8E',
            '&:hover': {
              color: '#4A90E2',
              backgroundColor: 'rgba(74, 144, 226, 0.08)',
            },
          }}
        >
          <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        disableAutoFocusItem
        disableScrollLock
        anchorEl={exportMenuAnchorEl}
        open={isExportMenuOpen}
        onClose={handleCloseExportMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#FFF',
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.20)',
            borderRadius: '8px',
          },
        }}
      >

        <MenuItem onClick={handleExportCsv}>
          <ListItemIcon>
            <FileDownloadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t('users.exportCsv')}
        </MenuItem>

        <MenuItem onClick={handleCopyToClipboard}>
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t('users.copyClipboard')}
        </MenuItem>
      </Menu>
    </>
  );
};

const UserTable: React.FC<UserTableProps> = ({
  users,
  allGroups,
  onPageChange,
  filterModel,
  onFilterModelChange,
  openMenu,
  handleClick,
  handleClose,
  onInactivate,
  onResendInvite,
  onResendReminder,
  anchorEl,
  selectedUser,
  showModal,
  showEditModal,
  resolveRequesterDisplayName,
  exportFileCompanyName,
}) => {
  const { t } = useTranslation('settings');
  const appLocale = useAppLocale();
  const isInactivationReasonFilterActive = useMemo(
    () =>
      filterModel.items.some(
        (item) => item.field === 'inactivationReasonCode' && item.operator === 'isAnyOf'
      ),
    [filterModel.items]
  );

  const visibleRowsAfterNativeFilters = useMemo(
    () => filterUsersForExport(users, filterModel),
    [users, filterModel]
  );

  const shouldShowInactivationReasonColumn = useMemo(() => {
    if (isInactivationReasonFilterActive) {
      return true;
    }

    return visibleRowsAfterNativeFilters.some((user) => isInactiveStatus(user.status));
  }, [isInactivationReasonFilterActive, visibleRowsAfterNativeFilters]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: String(t('table.name')),
      flex: 2,
      minWidth: 220,
      filterOperators: simpleTextFilterOperators,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.4,
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Avatar
            sx={{
              background: 'linear-gradient(318deg, #00B2B5 16.44%, #96FFD2 85.22%)',
              color: '#3C4043',
              width: 35,
              height: 35,
              fontSize: '14px',
              marginRight: '8px',
              flexShrink: 0,
            }}
          >
            {params.row.initials}
          </Avatar>

          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: String(t('table.email')),
      flex: 2,
      minWidth: 150,
      filterOperators: simpleTextFilterOperators,
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
          }}
          title={params.row.email}
        >
          {params.row.email}
        </Box>
      ),
    },
    {
      field: 'group',
      headerName: String(t('table.groups')),
      flex: 2,
      minWidth: 220,
      valueGetter: (_value, row) => row.group || '',
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
          }}
          title={String(params.value || '')}
        >
          {String(params.value || ' - - ')}
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: String(t('table.registrationDate')),
      type: 'date',
      flex: 1,
      minWidth: 170,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => {
        if (!value) return null;

        const date = new Date(value as string);
        return Number.isNaN(date.getTime()) ? null : date;
      },
      valueFormatter: (value: any) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
          return ' - - ';
        }

        return formatDate(value, appLocale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
          }}
          title={String(params.formattedValue || ' - - ')}
        >
          {String(params.formattedValue || ' - - ')}
        </Box>
      ),
    },
    {
      field: 'inactivationReasonCode',
      headerName: String(t('table.inactivationReason')),
      type: 'singleSelect',
      flex: 2,
      minWidth: 260,
      align: 'left',
      headerAlign: 'left',
      valueOptions: sortedInactivationReasons.map((item) => item.reasonCode),
      getOptionLabel: (value: any) => getInactivationReasonLabel(value, t),
      valueGetter: (_value, row) => row.inactivationReasonCode || '',
      filterOperators: singleSelectOperators.filter((operator) => operator.value === 'isAnyOf'),
      cellClassName: 'disable-native-cell-title',
      renderCell: (params) => {
        const lastInactivation = getLastInactivationValue(params.row);
        const reasonCode = params.row.inactivationReasonCode || '';
        const reasonLabel = params.row.inactivationReasonLabel || '';

        if (!isInactiveStatus(params.row.status)) {
          return <Box sx={{ width: '100%' }}>- -</Box>;
        }

        return (
          <InactivationReasonCell
            last_inactivation={lastInactivation}
            reasonCode={reasonCode}
            reasonLabel={reasonLabel}
            resolveRequesterDisplayName={resolveRequesterDisplayName}
          />
        );
      },
    },
    {
      field: 'status',
      headerName: String(t('table.situation')),
      type: 'singleSelect',
      flex: 1,
      minWidth: 140,
      align: 'center',
      headerAlign: 'center',
      valueOptions: ['active', 'invited', 'inactive'],
      getOptionLabel: (value: any) => getStatusFilterLabel(value),
      valueGetter: (_value, row) => getStatusFilterValue(row.status),
      filterOperators: statusFilterOperators,
      renderCell: (params) => {
        switch (params.row.status) {
          case 1:
            return (
              <ChipStatus
                tooltipInfo=""
                status=""
                translationKey="userStatus.active"
                background="#BBF8BD"
                color="#338D31"
                icons={<RadioButtonChecked sx={{ fontSize: 15 }} />}
              />
            );

          case 2:
            return (
              <ChipStatus
                tooltipInfo=""
                status=""
                translationKey="userStatus.invited"
                background="#F8E7BB"
                color="#8D7931"
                icons={<Email sx={{ fontSize: 15 }} />}
              />
            );

          case 3:
          case 4:
            return (
              <ChipStatus
                tooltipInfo=""
                status=""
                translationKey="userStatus.inactive"
                background="#DDD"
                color="#878787"
                icons={<HideSource sx={{ fontSize: 15 }} />}
              />
            );

          case 5:
            return (
              <ChipStatus
                tooltipInfo=""
                status=""
                translationKey="userStatus.disabledBySupport"
                background="#DDD"
                color="#878787"
                icons={<HideSource sx={{ fontSize: 15 }} />}
              />
            );

          default:
            return null;
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.3,
      minWidth: 100,
      resizable: false,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <ExportHeaderButton
          companyName={exportFileCompanyName}
          users={users}
          allGroups={allGroups}
          filterModel={filterModel}
          resolveRequesterDisplayName={resolveRequesterDisplayName}
        />
      ),
      renderCell: (params) => (
        <Tooltip title={String(t('users.menuMore'))}>
          <IconButton onClick={(event) => handleClick(event, params.row)}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        name:
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}`
            : ' - - ',
        initials: `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase(),
        role: roleLabels[user.role] || '',
        email: user.email,
        group: getGroupValue(user.group),
        created_at: user.created_at,
        status: user.status,
        rawUser: user.rawUser,
        last_inactivation: isInactiveStatus(user.status) ? user.rawUser?.last_inactivation ?? null : null,
        inactivationReasonCode: isInactiveStatus(user.status)
          ? user.rawUser?.last_inactivation?.reason_code || ''
          : '',
        inactivationReasonLabel: isInactiveStatus(user.status)
          ? getInactivationReasonLabel(user.rawUser?.last_inactivation?.reason_code || '', t)
          : '',
      })),
    [users, t]
  );

  const singlePageSize = useMemo(() => {
    if (rows.length <= 0) return 1;
    return Math.min(rows.length, 100);
  }, [rows.length]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: singlePageSize,
  });

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: singlePageSize,
    });
  }, [singlePageSize]);

  const handlePaginationChange = (newModel: GridPaginationModel) => {
    const nextPage = rows.length <= singlePageSize ? 0 : newModel.page;

    setPaginationModel({
      page: nextPage,
      pageSize: singlePageSize,
    });

    onPageChange(nextPage + 1);
  };

  const statusFilterItem = filterModel.items.find((item) => item.field === 'status');

  const selectedStatusValues: StatusFilterValue[] =
    statusFilterItem?.operator === 'isAnyOf' && Array.isArray(statusFilterItem.value)
      ? statusFilterItem.value.filter(
          (value): value is StatusFilterValue =>
            value === 'active' || value === 'invited' || value === 'inactive'
        )
      : [];

  const hasActiveStatusFilter = selectedStatusValues.length > 0;

  const getStatusMenuItemColor = (option: StatusFilterValue) =>
    selectedStatusValues.includes(option) ? ACTIVE_FILTER_COLOR : INACTIVE_FILTER_COLOR;

  function UserStatusFilterColumnMenu(props: GridColumnMenuProps) {
    const applyNativeStatusFilter = (
      nextValues: StatusFilterValue[],
      event: React.MouseEvent<HTMLElement>
    ) => {
      props.hideMenu(event);

      setTimeout(() => {
        if (nextValues.length === 0) {
          onFilterModelChange({
            ...filterModel,
            items: [],
          });
          return;
        }

        onFilterModelChange({
          ...filterModel,
          items: [
            {
              id: 'status-filter',
              field: 'status',
              operator: 'isAnyOf',
              value: nextValues,
            },
          ],
        });
      }, 150);
    };

    const toggleNativeStatusFilter = (
      option: StatusFilterValue,
      event: React.MouseEvent<HTMLElement>
    ) => {
      const nextValues = selectedStatusValues.includes(option)
        ? selectedStatusValues.filter((value) => value !== option)
        : [...selectedStatusValues, option];

      applyNativeStatusFilter(nextValues, event);
    };

    if (props.colDef.field === 'status') {
      return (
        <Stack px={0.5} py={0.5}>
          {hasActiveStatusFilter && (
            <>
              <MenuItem
                onClick={(e) => {
                  applyNativeStatusFilter([], e);
                }}
              >
                <FilterListOff sx={{ fontSize: 15, marginRight: 1, color: INACTIVE_FILTER_COLOR }} />
              <span style={{ color: INACTIVE_FILTER_COLOR }}>{t('users.counters.showAllShort')}</span>
              </MenuItem>

              <Stack sx={{ px: 0.5, py: 0.5 }}>
                <Divider />
              </Stack>
            </>
          )}

          <MenuItem
            onClick={(e) => {
              toggleNativeStatusFilter('active', e);
            }}
          >
            <RadioButtonChecked
              sx={{
                fontSize: 15,
                marginRight: 1,
                color: getStatusMenuItemColor('active'),
              }}
            />
              <span style={{ color: getStatusMenuItemColor('active') }}>{t('users.counters.activeMany')}</span>
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              toggleNativeStatusFilter('invited', e);
            }}
          >
            <Email
              sx={{
                fontSize: 15,
                marginRight: 1,
                color: getStatusMenuItemColor('invited'),
              }}
            />
              <span style={{ color: getStatusMenuItemColor('invited') }}>{t('users.counters.invitedMany')}</span>
          </MenuItem>

          <MenuItem
            onClick={(e) => {
              toggleNativeStatusFilter('inactive', e);
            }}
          >
            <HideSource
              sx={{
                fontSize: 15,
                marginRight: 1,
                color: getStatusMenuItemColor('inactive'),
              }}
            />
              <span style={{ color: getStatusMenuItemColor('inactive') }}>{t('users.counters.inactiveOne')}</span>
          </MenuItem>
        </Stack>
      );
    }

    return (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuColumnsItem: null,
          columnMenuSortItem: null,
        }}
      />
    );
  }

  const NoRowsOverlay = () => (
    <Stack
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        textAlign: 'center',
      }}
      spacing={1}
    >
      <Typography variant="subtitle1" sx={{ color: '#3C4043', fontWeight: 600 }}>
        Nenhum usuário encontrado
      </Typography>

      <Typography variant="body2" sx={{ color: '#878B8E' }}>
        Não foi encontrado nenhum usuário que corresponda aos critérios de busca.
      </Typography>
    </Stack>
  );

  const NoResultsOverlay = () => (
    <Stack
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        textAlign: 'center',
      }}
      spacing={1}
    >
      <Typography variant="subtitle1" sx={{ color: '#3C4043', fontWeight: 600 }}>
        Nenhum resultado encontrado com os filtros aplicados
      </Typography>

      <Typography variant="body2" sx={{ color: '#878B8E' }}>
        Ajuste ou limpe os filtros para encontrar usuários que correspondam aos critérios de busca.
      </Typography>
    </Stack>
  );

  return (
    <ListTable>
      <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'visible' }}>
        <DataGrid
          autoHeight
          rowHeight={84}
          columnHeaderHeight={66}
          rows={rows}
          columns={columns}
          columnVisibilityModel={{
            inactivationReasonCode: shouldShowInactivationReasonColumn,
          }}
          getRowId={getUserRowId}
          localeText={getDataGridLocaleText(appLocale)}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          pageSizeOptions={[singlePageSize]}
          disableRowSelectionOnClick
          disableAutosize
          disableMultipleRowSelection
          disableColumnSelector
          ignoreDiacritics
          hideFooterSelectedRowCount
          slots={{
            columnMenu: UserStatusFilterColumnMenu,
            columnMenuIcon: FilterList,
            columnFilteredIcon: ActiveColumnFilteredIcon,
            noRowsOverlay: NoRowsOverlay,
            noResultsOverlay: NoResultsOverlay,
          }}
          slotProps={{
            basePopper: {
              sx: {
                '& .MuiPaper-root': {
                  borderRadius: '8px',
                  boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
                  border: '1px solid #ECEFF3',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  maxWidth: 'calc(100vw - 48px)',
                },
                '& .MuiDataGrid-panelWrapper': {
                  padding: '8px',
                  minWidth: '760px',
                },
                '& .MuiDataGrid-panelContent': {
                  overflow: 'visible !important',
                },
                '& .MuiDataGrid-filterForm': {
                  gap: '12px',
                  padding: '10px',
                  borderRadius: '12px',
                  marginBottom: '8px',
                  minWidth: '720px',
                  width: 'max-content',
                },
                '& .MuiDataGrid-filterFormColumnInput': {
                  minWidth: '190px',
                  flex: '0 0 190px',
                },
                '& .MuiDataGrid-filterFormOperatorInput': {
                  minWidth: '220px',
                  flex: '0 0 220px',
                },
                '& .MuiDataGrid-filterFormValueInput': {
                  minWidth: '260px',
                  flex: '0 0 260px',
                },
                '& .MuiInputBase-root': {
                  borderRadius: '10px',
                },
                '& .MuiFormLabel-root': {
                  fontWeight: 500,
                },
                '& .MuiDataGrid-panelFooter': {
                  padding: '8px 12px 12px',
                },

                /* cria "área rolável extra" real à direita */
                '& .MuiDataGrid-filterForm::after': {
                  content: '""',
                  display: 'block',
                  flex: '0 0 24px',
                  width: '24px',
                },

                '@media (max-width: 768px)': {
                  '& .MuiDataGrid-filterForm::after': {
                    flex: '0 0 12px',
                    width: '12px',
                  },
                },
              },
            },
          }}
          sx={{
            minWidth: 0,
            '& .MuiSelect-select.MuiTablePagination-select': { display: 'none' },
            '& .MuiInputBase-root.MuiTablePagination-input': { display: 'none' },
            '& .MuiTablePagination-selectLabel': { display: 'none !important' },
            '& .MuiDataGrid-footerContainer p': { margin: 1 },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '1em 1.2em',
              color: '#878B8E',
            },
            '& .disable-native-cell-title .MuiDataGrid-cellContent': {
              pointerEvents: 'none',
            },
            '& .MuiDataGrid-cell': {
              padding: '1em 1.2em',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'visible !important',
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible',
            },
          }}
        />
      </Box>

      <Menu
        disableAutoFocusItem
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#FFF',
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.20)',
            borderRadius: '8px',
          },
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {(selectedUser?.status === 1 || selectedUser?.status === 2) && (
          <div>
            <MenuItem
              onClick={() => {
                if (selectedUser) {
                  showEditModal?.(selectedUser);
                }
                handleClose();
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              {t('users.menuEdit')}
            </MenuItem>

            <MenuItem
              onClick={() => {
                if (selectedUser) {
                  showModal?.(selectedUser);
                }
                handleClose();
              }}
            >
              <ListItemIcon>
                <GroupAdd fontSize="small" />
              </ListItemIcon>
              {t('users.menuGroup')}
            </MenuItem>

            {selectedUser?.status === 2 && (
              <MenuItem
                onClick={() => {
                  onResendInvite?.();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <Email fontSize="small" sx={{ color: '#d8ae17' }} />
                </ListItemIcon>
                {t('users.menuInvite')}
              </MenuItem>
            )}

            {selectedUser?.status === 1 && (
              <MenuItem
                onClick={() => {
                  onResendReminder?.();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <Email fontSize="small" sx={{ color: '#d8ae17' }} />
                </ListItemIcon>
                {t('users.menuReminder')}
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                if (selectedUser) {
                  onInactivate(selectedUser);
                }
                handleClose();
              }}
            >
              <ListItemIcon>
                <PersonRemoveIcon fontSize="small" sx={{ color: 'rgba(190, 79, 79, 1)' }} />
              </ListItemIcon>
              {t('users.menuInactivate')}
            </MenuItem>
          </div>
        )}

        {(selectedUser?.status === 3 || selectedUser?.status === 4) && (
          <MenuItem
            onClick={() => {
              if (selectedUser) {
                onInactivate(selectedUser);
              }
              handleClose();
            }}
          >
            <ListItemIcon>
              <PersonAddAlt1 fontSize="small" sx={{ color: 'rgba(79, 145, 190, 1)' }} />
            </ListItemIcon>
            {t('users.menuActivate')}
          </MenuItem>
        )}
      </Menu>
    </ListTable>
  );
};

export default UserTable;
